import { useEffect } from 'react';
import { AudioBus } from '@goodtime/engine';
import { BOT_ARCHETYPES, type BotArchetype } from '@goodtime/shared';
import { Icon } from '../Icon';
import { CastAvatar } from './CastAvatar';
import { GESTURES } from './gestures';

export interface ProfileCardProps {
  archetype: BotArchetype;
  /** Lifetime stats for this archetype, persisted server-side. Optional —
   * pre-SSE wiring we render reasonable mock numbers. */
  matchesPlayed?: number;
  /** Rolling win rate. ~0.167 = fair share at 6 seats. */
  winRate?: number;
  /** Number of players who have favourited this archetype. */
  fanCount?: number;
  /** Whether THIS player has favourited the archetype. */
  isFavorited?: boolean;
  /** Called when player toggles the favourite heart. */
  onFavoriteToggle?: () => void;
  /** Dismiss the modal. Tapping outside the card also fires this. */
  onClose: () => void;
  /** Send a directed gesture to this castmate (2026-06-08). When set,
   *  the card shows a high-five / good-luck / cheers row. */
  onSendGesture?: (gestureId: string) => void;
}

/**
 * Modal that opens when a cast member's avatar is tapped. Shows the
 * archetype's accent-tinted card, slider bars (effective values),
 * lifetime stats, fan count, and the favourite-toggle heart.
 *
 * <p>Per UX Spec §3.8 + Bot Spec §7.5. The card's animated background
 * (subtle particle effect themed to the archetype's accent) is
 * deferred to a follow-up — this commit ships the static profile.
 */
export function ProfileCard({
  archetype,
  matchesPlayed = 0,
  winRate = 1 / 6,
  fanCount = 0,
  isFavorited = false,
  onFavoriteToggle,
  onSendGesture,
  onClose,
}: ProfileCardProps) {
  const meta = BOT_ARCHETYPES[archetype];

  // Esc to dismiss.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    AudioBus.shared.play('ui:modal_open');
    return () => {
      AudioBus.shared.play('ui:modal_close');
    };
  }, []);

  const handleClose = (): void => {
    AudioBus.shared.play('ui:click');
    onClose();
  };

  const handleFavourite = (): void => {
    AudioBus.shared.play('cast:favourite');
    onFavoriteToggle?.();
  };

  const fairShareLow = 1 / 6 - 0.05;
  const fairShareHigh = 1 / 6 + 0.05;
  const winRateIsFair = winRate >= fairShareLow && winRate <= fairShareHigh;

  return (
    <div
      onClick={handleClose}
      role="dialog"
      aria-label={`${meta.displayName} profile`}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 200,
        background: 'rgba(0, 0, 0, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'gtFadeIn 0.18s ease-out',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 320,
          background: '#1B2C56',
          borderRadius: 24,
          overflow: 'hidden',
          boxShadow: '0 30px 60px rgba(0,0,0,0.55)',
          animation: 'gtPop 0.22s cubic-bezier(0.34, 1.56, 0.64, 1)',
          border: `3px solid ${meta.accent}`,
          fontFamily: '"Fredoka", -apple-system, system-ui',
          color: '#E9F0FF',
        }}
      >
        {/* Hero — archetype-tinted radial gradient + avatar centred. */}
        <div
          style={{
            padding: '20px 16px 12px',
            background: `radial-gradient(circle at 50% 35%, ${meta.accent}55 0%, transparent 65%)`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <CastAvatar archetype={archetype} size={96} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <h2
              style={{
                fontSize: 22,
                fontWeight: 800,
                margin: 0,
                letterSpacing: 0.3,
              }}
            >
              {meta.displayName}
            </h2>
            <span style={{ fontSize: 16 }}>🤖</span>
          </div>
          <div
            style={{
              fontSize: 12,
              color: '#E9F0FF',
              opacity: 0.75,
              fontStyle: 'italic',
            }}
          >
            "{meta.tagline}"
          </div>
        </div>

        {/* Sliders — base values from BOT_ARCHETYPES; effective sliders
            from the server's BotPersonality once SSE is wired. */}
        <div style={{ padding: '8px 20px 4px' }}>
          <SliderBar
            label="⚔ Aggression"
            value={meta.sliders.aggression}
            accent={meta.accent}
          />
          <SliderBar
            label="🛡 Caution"
            value={meta.sliders.caution}
            accent={meta.accent}
          />
          <SliderBar
            label="💬 Sociality"
            value={meta.sliders.sociality}
            accent={meta.accent}
          />
          <SliderBar
            label="🎲 Risk"
            value={meta.sliders.risk}
            accent={meta.accent}
          />
        </div>

        {/* Stats. */}
        <div
          style={{
            padding: '12px 20px 4px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 8,
            fontSize: 11,
          }}
        >
          <Stat label="Played" value={`${matchesPlayed.toLocaleString()}`} />
          <Stat
            label="Win rate"
            value={`${(winRate * 100).toFixed(1)}%`}
            badge={winRateIsFair ? 'fair' : null}
          />
          <Stat label="Fans" value={fanCount.toLocaleString()} />
        </div>

        {/* Send a gesture — directed one-to-one to this castmate. They
            high-five back a beat later (server reciprocation). */}
        {onSendGesture ? (
          <div style={{ padding: '6px 16px 0' }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.6,
                textTransform: 'uppercase',
                opacity: 0.6,
                marginBottom: 6,
              }}
            >
              Send a gesture
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {GESTURES.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => {
                    AudioBus.shared.play('ui:click');
                    onSendGesture(g.id);
                  }}
                  aria-label={g.label}
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    padding: '8px 4px',
                    borderRadius: 12,
                    border: 'none',
                    cursor: 'pointer',
                    background: 'rgba(255,255,255,0.06)',
                    color: '#E9F0FF',
                    fontFamily: 'inherit',
                  }}
                >
                  <img
                    src={g.src}
                    alt=""
                    width={30}
                    height={30}
                    draggable={false}
                    style={{ display: 'block' }}
                  />
                  <span style={{ fontSize: 10, fontWeight: 700 }}>{g.label}</span>
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {/* CTA row — Favourite + Close. */}
        <div style={{ padding: '12px 16px 16px', display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={handleFavourite}
            style={{
              flex: 1,
              padding: '10px 14px',
              borderRadius: 14,
              border: 'none',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              background: isFavorited
                ? `linear-gradient(180deg, ${meta.accent}, ${meta.accent}cc)`
                : 'rgba(255,255,255,0.08)',
              color: isFavorited ? '#fff' : '#E9F0FF',
              boxShadow: isFavorited
                ? `0 4px 0 ${meta.accent}55, 0 6px 12px rgba(0,0,0,0.3)`
                : 'none',
              transition: 'transform 0.1s',
              fontFamily: 'inherit',
              letterSpacing: 0.3,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            {/* Commissioned heart pair (asset brief §7 — heart.png /
                heart_outline.png). Filled = favourited; outline =
                not. Replaces the prior ♥ / ♡ Unicode glyphs. */}
            <Icon
              name={isFavorited ? 'heart' : 'heart_outline'}
              size={16}
              alt={isFavorited ? 'favourited' : 'not favourited'}
            />
            Favourite
          </button>
          <button
            type="button"
            onClick={handleClose}
            style={{
              padding: '10px 18px',
              borderRadius: 14,
              border: 'none',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              background: 'rgba(255,255,255,0.08)',
              color: '#E9F0FF',
              fontFamily: 'inherit',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function SliderBar({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent: string;
}) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        margin: '7px 0',
      }}
    >
      <span style={{ fontSize: 11, fontWeight: 600, width: 100, opacity: 0.85 }}>
        {label}
      </span>
      <div
        style={{
          flex: 1,
          height: 8,
          background: 'rgba(255,255,255,0.08)',
          borderRadius: 8,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: `linear-gradient(90deg, ${accent}aa, ${accent})`,
            transition: 'width 0.3s',
          }}
        />
      </div>
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          opacity: 0.7,
          minWidth: 28,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {pct.toFixed(0)}
      </span>
    </div>
  );
}

function Stat({
  label,
  value,
  badge,
}: {
  label: string;
  value: string;
  badge?: string | null;
}) {
  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.04)',
        borderRadius: 10,
        padding: '6px 8px',
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 9, opacity: 0.6, letterSpacing: 0.5, textTransform: 'uppercase' }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 800,
          marginTop: 2,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      {badge ? (
        <div
          style={{
            fontSize: 8,
            fontWeight: 700,
            color: '#9DE54E',
            marginTop: 2,
            textTransform: 'uppercase',
            letterSpacing: 0.6,
          }}
        >
          ({badge})
        </div>
      ) : null}
    </div>
  );
}
